import React, { useState, useEffect, useCallback } from "react";
import { useSelector } from "react-redux";
import { axiosNewInstance } from "../../config/Api";
import LangData from "../common/LangData";

const MyPolicies = () => {
    const token = localStorage.getItem("authToken");
    const mobile = useSelector((state) => state?.user?.loginuser?.mobile) || "";

    const [loading, setLoading] = useState(true);
    const [policies, setPolicies] = useState([]);
    const [error, setError] = useState(null);

    // Details Modal State
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [detailsLoading, setDetailsLoading] = useState(false);
    const [detailsError, setDetailsError] = useState(null);
    const [detailsData, setDetailsData] = useState(null);
    const [selectedPolicyNo, setSelectedPolicyNo] = useState(null);

    const handleOpenDetails = async (policyNo) => {
        if (!token) return;
        setSelectedPolicyNo(policyNo);
        setDetailsOpen(true);
        setDetailsLoading(true);
        setDetailsError(null);
        setDetailsData(null);
        try {
            const response = await axiosNewInstance.post(
                `api/policyDetails/${encodeURIComponent(policyNo)}`,
                null,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        Accept: "application/json",
                        "Content-Type": "application/json",
                    },
                }
            );
            setDetailsData(response?.data ?? null);
        } catch (err) {
            setDetailsError(err?.response?.data?.message || err?.message || "Unable to fetch policy details.");
        } finally {
            setDetailsLoading(false);
        }
    };

    const handleCloseDetails = () => {
        setDetailsOpen(false);
        setDetailsError(null);
        setDetailsData(null);
        setSelectedPolicyNo(null);
    };

    const fetchMyPolicies = useCallback(async () => {
        if (!token) return;

        setLoading(true);
        setError(null);

        const mobileNo = mobile.toString().trim().replace(/\D/g, "");

        if (!mobileNo) {
            setError("Mobile number not found for the logged-in user.");
            setLoading(false);
            return;
        }

        try {
            const response = await axiosNewInstance.post(
                "api/customerDetailsCustomer",
                {
                    referenceId: "5",
                    mobileNo: mobileNo
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            const details = response?.data?.policyDetails || response?.data?.policy_details || [];
            setPolicies(Array.isArray(details) ? details : []);
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Failed to fetch policies.");
        } finally {
            setLoading(false);
        }
    }, [token, mobile]);

    useEffect(() => {
        fetchMyPolicies();
    }, [fetchMyPolicies]);

    return (
        <div className="container mt-4">
            <div className="card p-3 shadow-sm">
                <h5 className="font-weight-bold fs-4 my-3 text-left">
                    <LangData keyname="label_my_policies" />
                </h5>

                {loading ? (
                    <div className="text-center p-5">
                        <span className="spinner-border spinner-border-lg" />
                        <p className="mt-2 text-muted">Loading your policies...</p>
                    </div>
                ) : error ? (
                    <div className="alert alert-danger py-2">{error}</div>
                ) : (
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead>
                                <tr>
                                    <th>#</th>
                                    {policies.length > 0 && Object.keys(policies[0]).map((col) => (
                                        <th key={col} style={{ textTransform: "none" }}>{col}</th>
                                    ))}
                                    <th><LangData keyname="label_action" /></th>
                                </tr>
                            </thead>
                            <tbody>
                                {policies.length > 0 ? (
                                    policies.map((policy, index) => (
                                        <tr key={index}>
                                            <td>{index + 1}</td>
                                            {Object.keys(policy).map((col) => (
                                                <td key={col}>
                                                    {typeof policy[col] === "object" && policy[col] !== null
                                                        ? JSON.stringify(policy[col])
                                                        : String(policy[col] ?? "")}
                                                </td>
                                            ))}
                                            <td>
                                                <button
                                                    className="btn btn-sm btn-primary"
                                                    onClick={() => handleOpenDetails(policy.policyNo || policy.policy_no || Object.values(policy)[0])}
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="100%" className="text-center text-muted">
                                            No policies found for mobile: {mobile}
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Policy Details Modal */}
            {detailsOpen && (
                <>
                    <div className="modal-backdrop fade show"></div>
                    <div className="modal fade show" style={{ display: "block" }} tabIndex="-1" role="dialog">
                        <div className="modal-dialog modal-lg" role="document">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">
                                        Policy Details {selectedPolicyNo ? `- ${selectedPolicyNo}` : ""}
                                    </h5>
                                    <button type="button" className="close" onClick={handleCloseDetails}>
                                        <span aria-hidden="true">&times;</span>
                                    </button>
                                </div>
                                <div className="modal-body">
                                    {detailsLoading && (
                                        <div className="text-center p-3">
                                            <span className="spinner-border spinner-border-sm mr-2" />
                                            Loading details...
                                        </div>
                                    )}
                                    {detailsError && <div className="alert alert-danger p-2">{detailsError}</div>}
                                    {!detailsLoading && detailsData && (
                                        <div className="table-responsive">
                                            <table className="table table-sm table-bordered">
                                                <tbody>
                                                    {Object.entries(detailsData).map(([key, value]) => (
                                                        <tr key={key}>
                                                            <th scope="row" style={{ width: "35%", background: "#f8f9fa", textTransform: "none" }}>
                                                                {key}
                                                            </th>
                                                            <td>
                                                                {typeof value === "object" && value !== null
                                                                    ? JSON.stringify(value)
                                                                    : String(value ?? "N/A")}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default MyPolicies;
